import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import GenAiModalShell from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/GenAiModalShell';
import DefineTaskStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/DefineTaskStep';
import RecordsConfigStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/RecordsConfigStep';
import ReviewStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/ReviewStep';
import { createDefaultSettings } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsSection';
import useDatasetNameField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/useDatasetNameField';
import {
    generationSessionConfig,
    settingsFromConfig,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/generationSessionPrefill';
import useGenerationSessions from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationSessions';
import useGenerationTargets from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationTargets';
import useRecordsState from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useRecordsState';
import { EMPTY_DATASET_BANNER_TEXT } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import { reportRequiredFieldsError } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/requiredFieldsError';
import { fetchGenerationDatasetNameSuggestion } from '@/features/datasets/utils/aiGenerationDataset';
import { targetNamesFromConfig } from '@/features/datasets/utils/datasetSourceConfig';
import { retrievalTaskTypeLabel, sessionOptionTitle } from '@/features/datasets/utils/retrievalAgentDataset';

/** Define Task info banner: counts when data exists, empty-state copy otherwise. */
export const formatRecordsBanner = (targets, emptyText = EMPTY_DATASET_BANNER_TEXT) => {
    const total = (targets || []).reduce((sum, target) => sum + (target.total || 0), 0);
    if (!targets?.length || !total) return emptyText;
    const parts = targets.map(
        (target) => `${(target.total || 0).toLocaleString('en-US')} ${target.name}`
    );
    const joined =
        parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
    return (
        `You have ${total.toLocaleString('en-US')} records: ${joined}. ` +
        'Generate additional records to expand or rebalance your dataset.'
    );
};

/**
 * Single-label classification flow (Figma cls frames): Define Task (goal,
 * custom guidelines, Active Labels, Advanced settings) → Records to generate
 * + per-label configurations (description, Examples teacher prompt, advanced
 * Exclude/Text length/Language/Tone) → Review & Generate.
 */
const ClassificationFlow = ({ open, onClose, onGenerate, usecase, warningBanner, disabled = false }) => {
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [goal, setGoal] = useState('');
    const [description, setDescription] = useState('');
    const [goalError, setGoalError] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);
    const [settings, setSettings] = useState(createDefaultSettings());
    const [footerError, setFooterError] = useState('');
    const [loadedSessionTitle, setLoadedSessionTitle] = useState(undefined);

    const sessions = useGenerationSessions(usecase?._id, open);
    const { datasetName, onDatasetNameChange, datasetNameError, validateDatasetName } = useDatasetNameField({
        open,
        usecaseId: usecase?._id,
        fetchSuggestion: fetchGenerationDatasetNameSuggestion,
    });

    const { targets } = useGenerationTargets(usecase, open);
    const targetNames = useMemo(() => targets.map((target) => target.name), [targets]);
    // null until the user edits the Active Labels; falls back to the usecase's labels.
    const [editedNames, setEditedNames] = useState(null);
    const activeNames = editedNames ?? targetNames;
    const labelOptions = useMemo(
        () => [...new Set([...targetNames, ...activeNames])],
        [targetNames, activeNames]
    );
    const records = useRecordsState(activeNames);

    const reset = () => {
        setStep(0);
        setGoal('');
        setDescription('');
        setGoalError(false);
        setDescriptionError(false);
        setSettings(createDefaultSettings());
        setEditedNames(null);
        setFooterError('');
        setLoadedSessionTitle(undefined);
    };

    const handleLabelNamesChange = (next) => {
        setEditedNames(next);
        setFooterError('');
    };

    /** Prefill the wizard from a past generation configuration (Dataset Name keeps its fresh default). */
    const applySession = (session) => {
        const config = generationSessionConfig(session);
        if (!config) return;
        setLoadedSessionTitle(sessionOptionTitle(session));
        setGoal(config.goal);
        setGoalError(false);
        setDescription(config.description);
        setDescriptionError(false);
        setSettings(settingsFromConfig(config));
        const names = targetNamesFromConfig(config);
        if (names.length) setEditedNames(names);
        records.prefill({
            names: names.length ? names : activeNames,
            totalRecords: config.datasetSize,
            counts: config.recordCounts,
            configs: config.targetConfigs,
        });
        setFooterError('');
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const validateStep0 = () => {
        const nextGoalError = !goal.trim();
        const nextDescriptionError = !description.trim();
        const datasetNameValid = validateDatasetName();
        setGoalError(nextGoalError);
        setDescriptionError(nextDescriptionError);
        return !nextGoalError && !nextDescriptionError && datasetNameValid;
    };

    /** Per-label examples are optional (aligned with the Retrieval Agent). */
    const validateStep1 = () => !records.totalRecordsError;

    const handlePrimary = async () => {
        if (step === 0) {
            if (validateStep0()) {
                setFooterError('');
                setStep(1);
            } else {
                setFooterError(reportRequiredFieldsError());
            }
            return;
        }
        if (step === 1) {
            if (validateStep1()) {
                setFooterError('');
                setStep(2);
            } else {
                setFooterError(reportRequiredFieldsError());
            }
            return;
        }
        try {
            setIsSubmitting(true);
            await onGenerate({
                datasetName: datasetName.trim(),
                goal: goal.trim(),
                description: description.trim(),
                totalRecords: records.totalRecords,
                recordCounts: records.counts,
                targetConfigs: records.configs,
                settings,
            });
            handleClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GenAiModalShell
            open={open}
            onClose={handleClose}
            title="Generate Data with AI"
            subtitle="Define what the dataset should teach, then configure the labels and examples that will guide generation."
            primaryLabel={step === 2 ? 'Generate' : 'Next'}
            onPrimary={handlePrimary}
            primaryDisabled={disabled}
            primaryDisableWhenReadOnly={step === 2}
            isSubmitting={isSubmitting}
            showBack={step > 0}
            onBack={() => {
                setFooterError('');
                setStep(step - 1);
            }}
            footerError={footerError}
        >
            {warningBanner}
            {step === 0 ? (
                <DefineTaskStep
                    bannerText={formatRecordsBanner(targets)}
                    datasetName={datasetName}
                    onDatasetNameChange={onDatasetNameChange}
                    datasetNameError={datasetNameError}
                    sessions={sessions}
                    loadedSessionTitle={loadedSessionTitle}
                    onLoadSession={applySession}
                    goal={goal}
                    description={description}
                    goalError={goalError}
                    descriptionError={descriptionError}
                    onGoalChange={(value) => {
                        setGoal(value);
                        setGoalError(false);
                        setFooterError('');
                    }}
                    onDescriptionChange={(value) => {
                        setDescription(value);
                        setDescriptionError(false);
                        setFooterError('');
                    }}
                    labelHeading="Active Labels"
                    labelOptions={labelOptions}
                    labelNames={activeNames}
                    onLabelNamesChange={handleLabelNamesChange}
                    settings={settings}
                    onSettingsChange={setSettings}
                    showTextLengthOptionHelp
                />
            ) : null}
            {step === 1 ? (
                <RecordsConfigStep
                    variant="classification"
                    targetNames={activeNames}
                    totalRecords={records.totalRecords}
                    onTotalRecordsChange={records.onTotalRecordsChange}
                    counts={records.counts}
                    onCountChange={records.onCountChange}
                    configs={records.configs}
                    onConfigChange={records.onConfigChange}
                    totalRecordsError={records.totalRecordsError}
                />
            ) : null}
            {step === 2 ? (
                <ReviewStep
                    goal={goal.trim()}
                    customGuidelines={description.trim()}
                    languages={settings.languages}
                    taskTypeLabel={retrievalTaskTypeLabel(usecase)}
                    totalRecords={records.totalRecords}
                />
            ) : null}
        </GenAiModalShell>
    );
};

ClassificationFlow.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onGenerate: PropTypes.func.isRequired,
    usecase: PropTypes.object,
    warningBanner: PropTypes.node,
    disabled: PropTypes.bool,
};

export default ClassificationFlow;
